import { useState } from "react";
import { toast } from "react-toastify";
import type { VenueData } from "../../types/venues.types";

interface UseMergeVenuesProps {
  apiService: {
    post: <T>(url: string, data?: object | FormData) => Promise<{ data: T }>;
  };
  onSuccess?: (updatedVenue: VenueData) => void;
}

interface MergeResponse {
  detail: string;
  venue: VenueData;
}

interface UseMergeVenuesReturn {
  mergeVenues: (
    primaryId: number,
    secondaryVenueId: number,
    fieldSelections: Record<string, "primary" | "secondary">,
  ) => Promise<void>;
  merging: boolean;
  error: string | null;
}

export const useMergeVenues = ({
  apiService,
  onSuccess,
}: UseMergeVenuesProps): UseMergeVenuesReturn => {
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mergeVenues = async (
    primaryId: number,
    secondaryVenueId: number,
    fieldSelections: Record<string, "primary" | "secondary">,
  ) => {
    try {
      setMerging(true);
      setError(null);
      const response = await apiService.post<MergeResponse>(
        `/api/venues/venue/${primaryId}/merge/`,
        {
          secondary_venue_id: secondaryVenueId,
          field_selections: fieldSelections,
        },
      );
      toast.success("Venues merged successfully!", {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
      });
      if (onSuccess && response.data.venue) {
        onSuccess(response.data.venue);
      }
    } catch (err) {
      const errorMessage =
        (err as { response?: { data?: { detail?: string; message?: string } } })
          ?.response?.data?.detail ||
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ||
        "Failed to merge venues. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setMerging(false);
    }
  };

  return { mergeVenues, merging, error };
};

export default useMergeVenues;
